import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import type { FieldValues, Path, RegisterOptions, PathValue } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import Field from '@/components/common/Field';

export interface RadioOption {
    value: string;
    label: ReactNode;
    id?: string;
    disabled?: boolean;
}

export interface RadioFieldProps<TFormValues extends FieldValues = FieldValues> {
    name: Path<TFormValues>;
    options: RadioOption[];
    label?: string;
    rules?: Omit<
        RegisterOptions<TFormValues, Path<TFormValues>>,
        'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
    >;
    defaultValue?: PathValue<TFormValues, Path<TFormValues>>;
    className?: string;
    required?: boolean;
}

const RadioField = <TFormValues extends FieldValues = FieldValues>({
    name,
    label,
    options,
    defaultValue,
    className,
    required,
}: RadioFieldProps<TFormValues>) => {
    const { getFieldState, formState, register } = useFormContext<TFormValues>();
    const fieldState = getFieldState(name, formState);

    return (
        <Field
            name={name}
            id={name}
            label={label}
            className={className}
            required={required}
            errorElement={fieldState.error?.message}
        >
            <div className="mt-5 inline-flex items-end justify-start gap-4 sm:mt-0">
                {options.map(option => (
                    <div
                        key={option.value}
                        className={clsx(
                            'flex h-6 items-center justify-start gap-2.5',
                            option.disabled && 'cursor-not-allowed opacity-60'
                        )}
                    >
                        <input
                            type="radio"
                            id={`${name}-${option.value}`}
                            value={option.value}
                            defaultChecked={option.value === defaultValue}
                            disabled={option.disabled}
                            className="bg-neutral-0 size-[15px] border-neutral-300 checked:border-teal-600 checked:bg-teal-600"
                            {...register(name)}
                        />
                        <label
                            htmlFor={`${name}-${option.value}`}
                            className={clsx(
                                'text-base leading-6 font-semibold text-neutral-900',
                                option.disabled && 'cursor-not-allowed'
                            )}
                        >
                            {option.label}
                        </label>
                    </div>
                ))}
            </div>
        </Field>
    );
};

export default RadioField;
